import { useEffect, useState } from "react";
import { Redirect } from "wouter";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";

/**
 * Phase 14 — Marketplace claim-code list / void.
 *
 * Tenant view of every code generated by an upload. Filter by
 * platform + status, see "claimed by", and (for CLAIMED codes only)
 * void → reverses the loyalty credit via tenant_adjustments.
 *
 * Pagination: cursor-based to mirror Phase 11's transactions feed.
 */

type Platform = "SHOPEE" | "LAZADA" | "TIKTOK" | "OTHER";
type Status = "UNCLAIMED" | "CLAIMED" | "EXPIRED" | "VOIDED";

interface ListItem {
  code: string;
  platform: Platform;
  status: Status;
  externalOrderId: string;
  grossAmountCentavos: number;
  currency: string;
  expiresAt: string;
  claimedAt: string | null;
  claimedByCustomerId: string | null;
  claimedByCustomerName: string | null;
}

interface ListResponse {
  items: ListItem[];
  nextCursor: string | null;
}

const PLATFORMS: Platform[] = ["SHOPEE", "LAZADA", "TIKTOK", "OTHER"];
const STATUSES: Status[] = ["UNCLAIMED", "CLAIMED", "EXPIRED", "VOIDED"];

function statusColor(s: Status): string {
  switch (s) {
    case "CLAIMED":
      return "bg-green-100 text-green-800";
    case "UNCLAIMED":
      return "bg-blue-100 text-blue-800";
    case "EXPIRED":
      return "bg-stone-200 text-stone-700";
    case "VOIDED":
      return "bg-red-100 text-red-800";
  }
}

export function MerchantMarketplaceCodesPage() {
  if (!getMerchantToken()) return <Redirect to="/merchant/login" />;

  const [platform, setPlatform] = useState<Platform | "">("");
  const [status, setStatus] = useState<Status | "">("");
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [voiding, setVoiding] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void (async () => {
      const sp = new URLSearchParams();
      sp.set("limit", "25");
      if (platform) sp.set("platform", platform);
      if (status) sp.set("status", status);
      const r = await merchantFetch<ListResponse>(
        `/api/v1/tenant/marketplace/codes?${sp.toString()}`,
      );
      if (cancelled) return;
      if (r.ok) setData(r.data);
      else setErr(r.message);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [platform, status, tick]);

  async function voidCode(code: string) {
    if (!confirm(`Void ${code}? This will reverse any points or stamps already credited.`)) {
      return;
    }
    setVoiding(code);
    const r = await merchantFetch(
      `/api/v1/tenant/marketplace/codes/${encodeURIComponent(code)}/void`,
      {
        method: "POST",
        body: JSON.stringify({ reason: "Manual void from merchant dashboard." }),
      },
    );
    setVoiding(null);
    if (!r.ok) {
      alert(`Void failed: ${r.message}`);
      return;
    }
    setTick((t) => t + 1);
  }

  return (
    <main className="mx-auto max-w-5xl p-4 md:p-6">
      <MerchantNav current="marketplace" />
      <h1 className="mt-6 text-2xl font-bold text-navy">Claim codes</h1>
      <p className="mt-1 text-sm text-ink-dark">
        Codes generated from your marketplace uploads. Void any code to
        reverse the loyalty credit.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div>
          <label className="label text-xs">Platform</label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as Platform | "")}
            className="mt-1 rounded border border-stone-300 px-2 py-1"
          >
            <option value="">All</option>
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label text-xs">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as Status | "")}
            className="mt-1 rounded border border-stone-300 px-2 py-1"
          >
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <a
          href="/merchant/marketplace"
          className="ml-auto text-sm text-navy underline"
        >
          ← Back to import
        </a>
      </div>

      {loading && <p className="mt-6 text-sm text-stone-500">Loading…</p>}
      {err && (
        <div className="mt-4 rounded bg-red-50 p-3 text-sm text-red-700">
          {err}
        </div>
      )}

      {data && data.items.length === 0 && !loading && (
        <p className="mt-8 text-sm text-stone-500">
          No codes match this filter. Try broadening it or upload a new
          batch from the Import tab.
        </p>
      )}

      {data && data.items.length > 0 && (
        <div className="mt-4 overflow-x-auto rounded border border-stone-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-left text-xs uppercase text-stone-600">
              <tr>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Platform</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Order #</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Claimed by</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.code} className="border-t border-stone-100">
                  <td className="px-3 py-2 font-mono">{it.code}</td>
                  <td className="px-3 py-2">{it.platform}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${statusColor(it.status)}`}
                    >
                      {it.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-stone-600">
                    {it.externalOrderId}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {(it.grossAmountCentavos / 100).toFixed(2)} {it.currency}
                  </td>
                  <td className="px-3 py-2 text-xs text-stone-600">
                    {it.claimedByCustomerName ??
                      (it.claimedAt ? "(unknown)" : "—")}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {it.status === "CLAIMED" && (
                      <button
                        type="button"
                        disabled={voiding === it.code}
                        onClick={() => voidCode(it.code)}
                        className="rounded border border-red-600 px-2 py-1 text-xs text-red-600 hover:bg-red-600 hover:text-white disabled:opacity-50"
                      >
                        {voiding === it.code ? "Voiding…" : "Void"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
